import {
  Activity,
  BriefcaseBusiness,
  CalendarCheck,
  ChartPie,
  Check,
  Code,
  FileText,
  Funnel,
  Globe,
  Hand,
  MailCheck,
  Megaphone,
  MousePointerClick,
  Plus,
  Search,
  ShieldCheck,
  Target,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type RefObject } from 'react';
import { Badge, dotTone } from '@/components/ui/badge';
import { usePrefersReducedMotion } from '@/components/landing/live-desk';
import { intlLocale, t, tr } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { AgentAvatar } from '@/components/agent-avatar';

/** A demo metric with a fixed number of decimals, in the page's language. */
const num = (n: number, digits = 0) => n.toLocaleString(intlLocale, { minimumFractionDigits: digits, maximumFractionDigits: digits });

type Tone = 'violet' | 'sky' | 'emerald';

const TONE: Record<Tone, { text: string; tile: string; avatar: string; bar: string; glow: string }> = {
  violet: {
    text: 'text-violet-600 dark:text-violet-400',
    tile: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    avatar: 'from-violet-500 to-fuchsia-500',
    bar: 'bg-violet-500',
    glow: 'bg-violet-500/20',
  },
  sky: {
    text: 'text-sky-600 dark:text-sky-400',
    tile: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
    avatar: 'from-sky-500 to-indigo-500',
    bar: 'bg-sky-500',
    glow: 'bg-sky-500/20',
  },
  emerald: {
    text: 'text-emerald-600 dark:text-emerald-400',
    tile: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    avatar: 'from-emerald-500 to-teal-500',
    bar: 'bg-emerald-500',
    glow: 'bg-emerald-500/20',
  },
};

interface DeskDef {
  name: string;
  icon: LucideIcon;
  agents: string[];
  trigger: string;
  metric: { label: string; value: (tick: number) => string };
  events: string[];
  approval?: { ask: string; done: string };
}

interface WorkspaceDef {
  id: string;
  name: string;
  shortName: string;
  icon: LucideIcon;
  tone: Tone;
  tagline: string;
  /** What someone types to add the next desk. */
  nextDesk: string;
  actionsToday: number;
  desks: DeskDef[];
  handoffs: { from: string; to: string; what: string }[];
}

const WORKSPACES: WorkspaceDef[] = [
  {
    id: 'marketing',
    name: t('Marketing'),
    shortName: t('Marketing'),
    icon: Megaphone,
    tone: 'violet',
    tagline: t('Reach, traffic and pipeline from every channel'),
    nextDesk: t('Answer every new G2 review within a day.'),
    actionsToday: 1184,
    desks: [
      {
        name: 'SEO',
        icon: Search,
        agents: ['Nora', 'Ben'],
        trigger: t('daily 07:00'),
        metric: { label: t('Keywords in top 10'), value: (tick) => String(148 + Math.floor(tick / 5)) },
        events: [t('Audited 32 pages, fixed 4 meta titles'), t('Brief ready: “AI desks for support teams”'), t('“ai support agent” climbed to #6'), t('Added internal links to 12 posts')],
        approval: { ask: t('Publish 2 rewritten pages?'), done: t('You approved: 2 pages are live') },
      },
      {
        name: 'SEA',
        icon: MousePointerClick,
        agents: ['Theo'],
        trigger: t('every hour'),
        metric: { label: 'ROAS', value: (tick) => `${num(4.1 + Math.floor(tick / 4) * 0.03, 2)}×` },
        events: [t('Paused 3 keywords with CPA over €80'), t('Started A/B test on 2 new ad texts'), t('Moved €120 budget to brand campaign'), t('Added 14 negative keywords')],
        approval: { ask: t('Raise daily budget to €450?'), done: t('You approved: budget is €450 a day') },
      },
      {
        name: 'Meta Ads',
        icon: Target,
        agents: ['Lia', 'Theo'],
        trigger: t('every 2 h'),
        metric: { label: t('Cost per lead'), value: (tick) => t('€{amount}', { amount: num(19.8 - Math.floor(tick / 4) * 0.15, 2) }) },
        events: [t('Creative fatigue on set B, rotated'), t('Built 2% lookalike from new segment'), t('3 new creatives ready for review'), t('Frequency capped at 3 per week')],
      },
      {
        name: 'LinkedIn',
        icon: BriefcaseBusiness,
        agents: ['Jonas', 'Ella'],
        trigger: t('Mon–Fri 09:00'),
        metric: { label: t('Engagement rate'), value: (tick) => t('{n}%', { n: num(5.8 + Math.floor(tick / 5) * 0.1, 1) }) },
        events: [t('Drafted “Why desks beat chatbots”'), t('Thought-leader ad live for 4 segments'), t('Answered 9 comments on Monday’s post'), t('Ella checked tone and facts')],
        approval: { ask: t('Publish Tuesday’s post?'), done: t('You approved: post scheduled 09:30') },
      },
      {
        name: t('Audience Analytics'),
        icon: ChartPie,
        agents: ['Sam'],
        trigger: t('daily 06:00'),
        metric: { label: t('Live segments'), value: (tick) => String(14 + Math.floor(tick / 9)) },
        events: [t('New segment: SaaS CTOs, DACH'), t('Weekly cohort report shared'), t('Churn risk up in “SMB retail”'), t('Synced segments to ad accounts')],
      },
    ],
    handoffs: [
      { from: t('Audience Analytics'), to: 'Meta Ads', what: t('Segment “SaaS CTOs, DACH”') },
      { from: 'SEO', to: 'LinkedIn', what: t('Post idea from a rising keyword') },
      { from: 'LinkedIn', to: t('Audience Analytics'), what: t('Who engaged with Tuesday’s post') },
    ],
  },
  {
    id: 'engineering',
    name: t('Software Engineering'),
    shortName: t('Engineering'),
    icon: Code,
    tone: 'sky',
    tagline: t('A fast, safe website without the ticket backlog'),
    nextDesk: t('Keep our docs in sync with every release.'),
    actionsToday: 642,
    desks: [
      {
        name: t('Website'),
        icon: Globe,
        agents: ['Max', 'Ida', 'Vera'],
        trigger: t('on every issue'),
        metric: { label: t('Lighthouse score'), value: (tick) => String(tick > 8 ? 98 : 97) },
        events: [t('Fixed layout shift on the pricing page'), t('Updated landing page copy from Marketing'), t('Vera reviewed the fix: approved'), t('Opened MR !214: new hero section'), t('Image sizes cut by 41%')],
        approval: { ask: t('Merge MR !214 to main?'), done: t('You approved: MR !214 is deployed') },
      },
      {
        name: t('Security'),
        icon: ShieldCheck,
        agents: ['Kai', 'Vera'],
        trigger: t('every 6 h'),
        metric: { label: t('Open findings'), value: (tick) => String(Math.max(0, 3 - Math.floor(tick / 5))) },
        events: [t('Dependency scan: 2 patches proposed'), t('Rotated an API token before expiry'), t('CVE-2026-4127 checked: not affected'), t('Tightened the CSP header'), t('Weekly access review sent')],
        approval: { ask: t('Apply patch to 2 dependencies?'), done: t('You approved: patches merged, tests green') },
      },
    ],
    handoffs: [
      { from: t('Security'), to: t('Website'), what: t('CSP header patch to ship') },
      { from: t('Website'), to: t('Security'), what: t('MR !214 for a security review') },
      { from: t('Security'), to: t('Website'), what: t('3 dependency updates to merge') },
    ],
  },
  {
    id: 'sales',
    name: t('Sales'),
    shortName: t('Sales'),
    icon: TrendingUp,
    tone: 'emerald',
    tagline: t('No lead waits, no deal goes quiet'),
    nextDesk: t('Win back customers who cancelled last quarter.'),
    actionsToday: 927,
    desks: [
      {
        name: t('Lead Qualification'),
        icon: Funnel,
        agents: ['Lena', 'Finn'],
        trigger: t('every 5 min'),
        metric: { label: t('Qualified today'), value: (tick) => String(18 + Math.floor(tick / 3)) },
        events: [t('Scored 14 new inbound leads'), t('Acme GmbH: ICP fit 92, qualified'), t('Disqualified 3: no budget signal'), t('Researched the buying committee at Nordwind')],
      },
      {
        name: t('Sales Monitor'),
        icon: Activity,
        agents: ['Ole'],
        trigger: t('every hour'),
        metric: { label: t('Open pipeline'), value: (tick) => t('€{n}k', { n: 412 + Math.floor(tick / 2) * 3 }) },
        events: [t('“Nordwind” stuck for 9 days: flagged'), t('Forecast updated: +6% vs. last week'), t('Win rate this month: 31%'), t('2 deals moved to negotiation')],
      },
      {
        name: t('Lead Follow-up'),
        icon: MailCheck,
        agents: ['Mia', 'Finn'],
        trigger: t('Mon–Fri 10:00'),
        metric: { label: t('Follow-ups sent'), value: (tick) => String(37 + Math.floor(tick / 2)) },
        events: [t('Drafted 8 personal follow-ups'), t('Müller AG replied: meeting booked'), t('Reminder set for silent leads'), t('Logged 5 calls in the CRM')],
        approval: { ask: t('Send 8 follow-up emails?'), done: t('You approved: 8 emails sent') },
      },
      {
        name: t('Meeting Prep'),
        icon: CalendarCheck,
        agents: ['Pia'],
        trigger: t('1 h before each call'),
        metric: { label: t('Briefs this week'), value: (tick) => String(11 + Math.floor(tick / 6)) },
        events: [t('Brief for the Müller AG call at 14:00 ready'), t('Pulled the last 3 emails and open tickets'), t('Suggested 3 discovery questions'), t('Summarised Nordwind call into the CRM')],
      },
      {
        name: t('Proposals'),
        icon: FileText,
        agents: ['Clara'],
        trigger: t('on deal stage'),
        metric: { label: t('Open proposals'), value: (tick) => String(6 + Math.floor(tick / 8)) },
        events: [t('Drafted Nordwind proposal from call notes'), t('Pricing checked against the playbook'), t('Proposal opened 3× by Müller AG'), t('Contract terms compared')],
        approval: { ask: t('Offer Nordwind 10% discount?'), done: t('You approved: proposal sent') },
      },
    ],
    handoffs: [
      { from: t('Lead Qualification'), to: t('Lead Follow-up'), what: t('5 qualified leads') },
      { from: t('Sales Monitor'), to: t('Lead Follow-up'), what: t('“Nordwind” needs a nudge') },
      { from: t('Lead Follow-up'), to: t('Proposals'), what: t('Müller AG asked for an offer') },
      { from: t('Meeting Prep'), to: t('Proposals'), what: t('Call notes from Nordwind') },
      { from: t('Proposals'), to: t('Lead Follow-up'), what: t('Offer for Müller AG sent') },
    ],
  },
];

type DeskStatus = 'working' | 'review' | 'approval' | 'done' | 'watching';

const STATUS: Record<DeskStatus, { label: string; tone: 'neutral' | 'blue' | 'accent' | 'amber' | 'green'; live?: boolean }> = {
  working: { label: t('Working'), tone: 'blue', live: true },
  review: { label: t('Checking'), tone: 'accent', live: true },
  approval: { label: t('Needs you'), tone: 'amber', live: true },
  done: { label: t('Done'), tone: 'green' },
  watching: { label: t('Watching'), tone: 'neutral' },
};

/** Every desk walks through this cycle, each one at its own offset so the workspace never moves in lockstep. */
const PHASES: DeskStatus[] = ['working', 'review', 'approval', 'done', 'watching'];

const TICK_MS = 700;
/** A desk moves one step every few ticks; desks are staggered by one tick each. */
const TICKS_PER_STEP = 4;
/** Frame shown without animation: a mix of statuses, including one approval. */
const STILL_TICK = 6;

const mod = (n: number, m: number) => ((n % m) + m) % m;

const TOTAL_AGENTS = new Set(WORKSPACES.flatMap((w) => w.desks.flatMap((d) => d.agents))).size;
const TOTAL_ACTIONS = WORKSPACES.reduce((sum, w) => sum + w.actionsToday, 0);

interface DeskState {
  status: DeskStatus;
  event: string;
  approved?: boolean;
}

function stateAt(desk: DeskDef, index: number, step: number): DeskState {
  const phase = PHASES[mod(step + index, PHASES.length)]!;
  const event = desk.events[mod(step, desk.events.length)]!;
  if (phase === 'watching') return { status: 'watching', event: t('Waiting for the next trigger · {trigger}', { trigger: desk.trigger }) };
  if (phase === 'approval') return desk.approval ? { status: 'approval', event: desk.approval.ask } : { status: 'working', event };
  if (phase === 'done' && desk.approval) return { status: 'done', event: desk.approval.done, approved: true };
  return { status: phase, event };
}

const stepOf = (tick: number, index: number) => Math.floor((tick + index) / TICKS_PER_STEP);

function useInView(ref: RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setInView(!!entry?.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  return inView;
}

/**
 * Animated landing-page mock of a company running three workspaces (Marketing, Engineering, Sales), stacked so
 * visitors see each one by scrolling. Every workspace runs its desks side by side, handing work to each other
 * within the workspace; it animates while on screen, pauses on hover and shows a still frame for reduced motion.
 */
export function WorkspacesShowcase({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-16 sm:space-y-24', className)}>
      {WORKSPACES.map((ws) => (
        <WorkspacePanel key={ws.id} ws={ws} />
      ))}
    </div>
  );
}

function WorkspacePanel({ ws }: { ws: WorkspaceDef }) {
  const reduced = usePrefersReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref);
  const [tick, setTick] = useState(reduced ? STILL_TICK : 0);
  const [hovered, setHovered] = useState(false);
  const running = !reduced && inView && !hovered;

  useEffect(() => {
    if (!running) return;
    const timer = setTimeout(() => setTick(tick + 1), TICK_MS);
    return () => clearTimeout(timer);
  }, [running, tick]);

  const tone = TONE[ws.tone];
  const states = ws.desks.map((d, i) => stateAt(d, i, stepOf(tick, i)));
  const needsYou = states.filter((s) => s.status === 'approval').length;
  const agentCount = new Set(ws.desks.flatMap((d) => d.agents)).size;
  const roomy = ws.desks.length <= 2;

  return (
    <div
      ref={ref}
      id={`workspace-${ws.id}`}
      className="relative scroll-mt-24"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div aria-hidden className={cn('pointer-events-none absolute -inset-x-10 -top-10 bottom-0 rounded-full opacity-70 blur-3xl', tone.glow)} />

      <div className="relative rounded-2xl border bg-card/80 p-1.5 shadow-2xl shadow-primary/10 backdrop-blur">
        <div className="grid overflow-hidden rounded-xl border bg-background md:grid-cols-[240px_1fr]">
          {/* Workspace list: this one is open, the others are a jump down or up the page */}
          <aside className="flex flex-col border-b bg-muted/30 p-3 md:border-r md:border-b-0">
            <div className="mb-3 hidden items-center gap-1.5 px-1 md:flex">
              <span className="size-2.5 rounded-full bg-red-400/80" />
              <span className="size-2.5 rounded-full bg-amber-400/80" />
              <span className="size-2.5 rounded-full bg-emerald-400/80" />
            </div>
            <div className="mb-2 hidden px-1 text-[11px] font-medium tracking-wider text-muted-foreground uppercase md:block">{t('Workspaces')}</div>
            <nav aria-label={t('Workspaces')} className="grid grid-cols-3 gap-1 md:grid-cols-1">
              {WORKSPACES.map((w) => {
                const active = w.id === ws.id;
                const wTone = TONE[w.tone];
                return (
                  <a
                    key={w.id}
                    href={`#workspace-${w.id}`}
                    aria-current={active ? 'true' : undefined}
                    className={cn(
                      'relative flex min-w-0 flex-col items-center gap-1 overflow-hidden rounded-lg p-2 text-center transition-all md:flex-row md:gap-2.5 md:text-left',
                      active ? 'bg-card shadow-sm ring-1 ring-border' : 'text-muted-foreground hover:bg-card/60 hover:text-foreground',
                    )}
                  >
                    <span className={cn('relative flex size-8 shrink-0 items-center justify-center rounded-lg transition-colors', active ? wTone.tile : 'bg-muted')}>
                      <w.icon className="size-4" />
                      <span className={cn('absolute -top-0.5 -right-0.5 size-2 rounded-full ring-2 ring-background', dotTone.green, active && running && 'animate-soft-pulse')} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={cn('block truncate text-[13px] font-medium', active && 'text-foreground')}>
                        <span className="md:hidden">{w.shortName}</span>
                        <span className="hidden md:inline">{w.name}</span>
                      </span>
                      <span className="hidden truncate text-[11px] text-muted-foreground md:block">{t('{n} desks', { n: w.desks.length })}</span>
                    </span>
                  </a>
                );
              })}
            </nav>
            <div className="mt-auto hidden pt-6 md:block">
              <div className="rounded-lg border bg-card p-3">
                <div className="text-[11px] font-medium tracking-wider text-muted-foreground uppercase">{t('Today, all workspaces')}</div>
                <div className="mt-1 text-xl font-semibold tabular-nums">{(TOTAL_ACTIONS + tick * 5).toLocaleString(intlLocale)}</div>
                <div className="text-xs text-muted-foreground">{t('actions by {n} agents', { n: TOTAL_AGENTS })}</div>
              </div>
            </div>
          </aside>

          {/* This workspace */}
          <div className="flex min-w-0 flex-col p-3 sm:p-4 md:min-h-[520px]">
            <div className="mb-4 flex flex-wrap items-start gap-x-4 gap-y-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold tracking-tight">{ws.name}</span>
                  <Badge tone="green">
                    <span className={cn('size-1.5 rounded-full', dotTone.green, 'animate-soft-pulse')} />
                    {t('{n} desks live', { n: ws.desks.length })}
                  </Badge>
                </div>
                <div className="mt-0.5 truncate text-xs text-muted-foreground">{ws.tagline}</div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="hidden text-muted-foreground sm:inline">
                  {tr('{agents} agents · {actions} actions today', {
                    agents: agentCount,
                    actions: <span className="tabular-nums">{(ws.actionsToday + tick * 2).toLocaleString(intlLocale)}</span>,
                  })}
                </span>
                {needsYou > 0 ? (
                  <Badge tone="amber" className="animate-in fade-in zoom-in-95">
                    <Hand /> {needsYou === 1 ? t('1 needs you') : t('{n} need you', { n: needsYou })}
                  </Badge>
                ) : (
                  <Badge tone="muted">
                    <Check /> {t('All clear')}
                  </Badge>
                )}
              </div>
            </div>

            <div className={cn('grid gap-3', roomy ? 'sm:grid-cols-2' : 'sm:grid-cols-2 xl:grid-cols-3')}>
              {ws.desks.map((desk, i) => (
                <DeskCard key={desk.name} desk={desk} index={i} tick={tick} state={states[i]!} tone={ws.tone} roomy={roomy} />
              ))}
              <NewDeskCard prompt={ws.nextDesk} delay={ws.desks.length * 70} typing={running} wide={roomy} />
            </div>

            <Handoff key={Math.floor(tick / 7)} handoff={ws.handoffs[Math.floor(tick / 7) % ws.handoffs.length]!} tone={ws.tone} />
          </div>
        </div>
      </div>
    </div>
  );
}

function DeskCard({ desk, index, tick, state, tone, roomy }: { desk: DeskDef; index: number; tick: number; state: DeskState; tone: Tone; roomy: boolean }) {
  const meta = STATUS[state.status];
  const toneStyle = TONE[tone];
  const step = stepOf(tick, index);
  const earlier = roomy ? [1, 2, 3].map((k) => stateAt(desk, index, step - k)) : [];

  return (
    <div
      style={{ animationDelay: `${index * 70}ms` }}
      className={cn(
        'flex min-w-0 flex-col rounded-xl border bg-card p-3.5 transition-[border-color,box-shadow] duration-500 animate-in fade-in slide-in-from-bottom-3 fill-mode-both',
        state.status === 'approval' && 'border-amber-300 shadow-lg shadow-amber-500/10 ring-2 ring-amber-400/25 dark:border-amber-700',
      )}
    >
      <div className="flex items-center gap-2.5">
        <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg', toneStyle.tile)}>
          <desk.icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold">{desk.name}</div>
          <div className="mt-1 flex -space-x-1">
            {desk.agents.map((a, i) => (
              <span key={a} title={a} className="rounded-full ring-2 ring-card">
                <AgentAvatar
                  seed={`${desk.name}-${a}`}
                  size={18}
                  lead={i === 0}
                  mood={state.status === 'approval' && i === 0 ? 'waiting' : state.status === 'watching' ? 'sleeping' : state.status === 'done' ? 'happy' : 'working'}
                  className="rounded-full"
                />
              </span>
            ))}
          </div>
        </div>
        <Badge tone={meta.tone} className="min-w-[84px] justify-center transition-colors duration-500">
          <span className={cn('size-1.5 rounded-full', dotTone[meta.tone], meta.live && 'animate-soft-pulse')} />
          {meta.label}
        </Badge>
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-lg leading-none font-semibold tabular-nums">{desk.metric.value(tick)}</div>
          <div className="mt-1 truncate text-[11px] text-muted-foreground">{desk.metric.label}</div>
        </div>
        <Sparkline seed={index + desk.name.length} className={toneStyle.text} />
      </div>

      <div className="mt-3 h-8">
        {state.status === 'approval' ? (
          <div className="flex h-full items-center gap-2 rounded-lg border border-amber-300/60 bg-amber-50 pr-1 pl-2.5 animate-in fade-in zoom-in-95 duration-300 dark:border-amber-800/60 dark:bg-amber-950/30">
            <Hand className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
            <span className="min-w-0 flex-1 truncate text-xs font-medium">{state.event}</span>
            <span className="relative inline-flex h-6 shrink-0 items-center rounded-md bg-primary px-2 text-[11px] font-medium text-primary-foreground">
              <span className="absolute inset-0 animate-ping rounded-md bg-primary/40 [animation-duration:1.8s]" />
              <span className="relative">{t('Approve')}</span>
            </span>
          </div>
        ) : (
          <div key={state.event} className="flex h-full items-center gap-1.5 border-t pt-2 text-xs animate-in fade-in slide-in-from-bottom-1 duration-500">
            {state.approved ? (
              <Check className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <span className={cn('size-1.5 shrink-0 rounded-full', dotTone[meta.tone])} />
            )}
            <span className={cn('truncate', state.status === 'watching' ? 'text-muted-foreground' : 'text-foreground')}>{state.event}</span>
          </div>
        )}
      </div>

      {roomy && (
        <ul className="mt-2 space-y-1.5">
          {earlier.map((s, k) => (
            <li key={`${step - k}-${s.event}`} className={cn('flex items-center gap-1.5 truncate text-xs text-muted-foreground', k === 2 && 'opacity-50')}>
              <span className="size-1 shrink-0 rounded-full bg-muted-foreground/50" />
              <span className="truncate">{s.status === 'approval' ? t('Asked you: {event}', { event: s.event }) : s.event}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Handoff({ handoff, tone }: { handoff: WorkspaceDef['handoffs'][number]; tone: Tone }) {
  const toneStyle = TONE[tone];
  return (
    <div className="mt-auto pt-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-dashed bg-muted/30 px-3 py-2.5 text-xs animate-in fade-in slide-in-from-bottom-1 duration-500">
        <span className="font-medium tracking-wider text-muted-foreground uppercase">{t('Handoff')}</span>
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <span className="truncate rounded-md border bg-card px-2 py-0.5 font-medium">{handoff.from}</span>
          <span className={cn('relative h-px min-w-10 flex-1 bg-gradient-to-r from-border via-current to-border', toneStyle.text)}>
            <span className={cn('animate-ws-travel absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-[0_0_12px_2px_currentColor]', toneStyle.bar)} />
          </span>
          <span className="truncate rounded-md border bg-card px-2 py-0.5 font-medium">{handoff.to}</span>
        </span>
        <span className="w-full truncate text-muted-foreground lg:w-auto lg:max-w-[40%]">{handoff.what}</span>
      </div>
    </div>
  );
}

/** Placeholder for the next desk: one sentence typed into the setup assistant. */
function NewDeskCard({ prompt, delay, typing, wide }: { prompt: string; delay: number; typing: boolean; wide: boolean }) {
  const [typed, setTyped] = useState(typing ? 0 : prompt.length);

  useEffect(() => {
    if (!typing || typed >= prompt.length) return;
    const timer = setTimeout(() => setTyped((n) => n + 1), 45);
    return () => clearTimeout(timer);
  }, [typing, typed, prompt]);

  return (
    <div
      style={{ animationDelay: `${delay}ms` }}
      className={cn(
        'flex min-w-0 gap-3 rounded-xl border border-dashed bg-muted/20 p-3.5 animate-in fade-in slide-in-from-bottom-3 fill-mode-both',
        wide ? 'flex-col sm:col-span-2 sm:flex-row sm:items-center' : 'flex-col justify-center',
      )}
    >
      <div className="flex shrink-0 items-center gap-2.5">
        <span className="flex size-8 items-center justify-center rounded-lg border border-dashed text-muted-foreground">
          <Plus className="size-4" />
        </span>
        <span className="text-[13px] font-semibold">{t('New desk')}</span>
      </div>
      <div className="min-w-0 flex-1 rounded-lg border bg-card px-2.5 py-2 text-xs">
        {tr('“{quote}”', {
          quote: (
            <>
              {prompt.slice(0, typed)}
              <span className={cn('ml-px inline-block h-3.5 w-px translate-y-0.5 bg-foreground', typed < prompt.length ? '' : 'animate-soft-pulse')} />
            </>
          ),
        })}
      </div>
      <div className="text-[11px] text-muted-foreground">{t('One sentence. The Lead staffs the rest.')}</div>
    </div>
  );
}

/** Periodic so the doubled path scrolls seamlessly. */
function sparkPath(seed: number) {
  const points: string[] = [];
  const n = 48;
  for (let i = 0; i <= n; i++) {
    const x = (i / n) * 200;
    const p = (x / 100) * Math.PI * 2;
    const y = 22 - 9 * Math.sin(p + seed) - 5 * Math.sin(p * 3 + seed * 2.1) - 2 * Math.sin(p * 7 + seed * 3.3);
    points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  const line = `M${points.join(' L')}`;
  return { line, area: `${line} L200,40 L0,40 Z` };
}

function Sparkline({ seed, className }: { seed: number; className?: string }) {
  const gradientId = `spark-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const { line, area } = useMemo(() => sparkPath(seed), [seed]);
  return (
    <div aria-hidden className={cn('relative h-9 w-24 shrink-0 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_20%,black_80%,transparent)]', className)}>
      <svg viewBox="0 0 200 40" preserveAspectRatio="none" className="animate-ws-marquee absolute inset-y-0 left-0 h-full w-[200%]" style={{ animationDuration: `${8 + (seed % 4)}s` }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="currentColor" stopOpacity="0.28" />
            <stop offset="1" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#${gradientId})`} />
        <path d={line} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
      </svg>
    </div>
  );
}
